import { computed } from 'vue';
import { useAppSettings } from './useAppSettings';

function getCurrencySymbol(currencyCode: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: currencyCode })
      .formatToParts(0)
      .find(part => part.type === 'currency')?.value ?? currencyCode;
  } catch {
    return currencyCode;
  }
}

/**
 * Composable for formatting decimal values
 */
export function useCurrencyFormatter() {
  const { appSettings } = useAppSettings();

  const numberLocale = computed(() => appSettings.value.locale);

  /**
   * Formats a number as decimal with localization (no currency symbol)
   * @param value - The numeric value to format
   * @returns Formatted decimal string
   */
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat(numberLocale.value, {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  /**
   * Formats a currency amount with +/- prefix and currency symbol
   * @param amount - The numeric amount to format
   * @returns Formatted currency string with prefix and symbol (e.g., "+123.45 €" or "-123.45 €" or "0.00 €")
   */
  const formatCurrencyWithSign = (amount: number): string => {
    const symbol = getCurrencySymbol(appSettings.value.currencyCode, numberLocale.value);
    const formattedAmount = formatCurrency(Math.abs(amount));
    let prefix = '';
    if (amount > 0) {
      prefix = '+';
    } else if (amount < 0) {
      prefix = '-';
    }
    return `${prefix}${formattedAmount} ${symbol}`;
  };

  /**
   * Formats a number as currency with dynamic currency symbol
   * @param value - The numeric value to format
   * @returns Formatted currency string with symbol (e.g., "123.45 €")
   */
  const formatCurrencyWithSymbol = (value: number): string => {
    const symbol = getCurrencySymbol(appSettings.value.currencyCode, numberLocale.value);
    return `${formatCurrency(value)} ${symbol}`;
  };

  const formatCurrencyWithEuro = (value: number | null | undefined): string => {
    if (value === null || value === undefined) {
      return '';
    }

    return formatCurrencyWithSymbol(value);
  };

  return {
    formatCurrency,
    formatCurrencyWithSign,
    formatCurrencyWithEuro,
    formatCurrencyWithSymbol,
    numberLocale,
  };
}
